import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import currentTimeZone from '../../config/timeZone';
import i18n from '../../i18n';
import ConvertDDMMYYYYtoISO from '../../shared-functions/ConvertDDMMYYYYtoISO';
// import ConvertUTCtoDateTime from '../../shared-functions/ConvertUTCtoDateTime';
import { permissionCheck } from '../../shared-functions/JWTDocodeFunction';
import tariffApi from '../../utils/api/tariff';
import BatteryLoader from '../battery-loader/BatteryLoader';
import MyButton from '../button/Button';

interface TariffSidePanelProperties {
  /** Source to fetch the tariff ID from ('queryParams' or 'props') */
  fetchIdFrom?: 'queryParams' | 'props';
  /** Tariff ID passed as a prop */
  id?: string;
  /** Flag indicating if the panel is opened from the Partner page */
  isPartner?: boolean;
}

/**
 * Convert UTC to date and time with seconds
 * @param {string} utcString - UTC string to convert
 * @returns {string} Date and time string
 */
const ConvertUTCtoDateTimeWithSeconds = (utcString: string | undefined | null): string => {
  if (!utcString) {
    return '';
  }
  const date = new Date(utcString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const options: Intl.DateTimeFormatOptions = {
    timeZone: currentTimeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  };
  try {
    return new Intl.DateTimeFormat(i18n.language, options).format(date);
  } catch (error) {
    console.log('Error formatting date:', error);
    return '';
  }
};

/**
 * Tariff side panel component to display detailed information for a selected tariff
 * @param {TariffSidePanelProperties} properties - Properties for TariffSidePanel component
 * @returns {React.JSX.Element} TariffSidePanel component
 */
function TariffSidePanel(properties: TariffSidePanelProperties = {}): React.JSX.Element {
  const { fetchIdFrom = 'queryParams', id, isPartner = false } = properties;
  const { t } = useTranslation('tariff');
  const [searchParameters] = useSearchParams();
  const navigate = useNavigate();
  const { id: parameterId } = useParams<{ id: string }>();

  const tariffId =
    fetchIdFrom === 'props'
      ? id || searchParameters.get('tariffId') || parameterId
      : searchParameters.get('tariffId') || id || parameterId;

  const [tariffDetails, setTariffDetails] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(true);


  useEffect(() => {
    if (tariffId) {
      setLoading(true);
      tariffApi
        .fetchSingleTariff(tariffId, isPartner)
        .then((response: any) => {
          const rawScopeLevels = response?.data?.scopeLevels;
          let parsedScopeLevels: any[] = [];
          if (Array.isArray(rawScopeLevels)) {
            parsedScopeLevels = rawScopeLevels;
          } else if (typeof rawScopeLevels === 'string') {
            try {
              parsedScopeLevels = JSON.parse(rawScopeLevels);
            } catch {
              parsedScopeLevels = [];
            }
          }

          const output = {
            status: response?.data?.status,
            enforcementDate: ConvertUTCtoDateTimeWithSeconds(response?.data?.effectiveFrom),
            expiryDate: ConvertUTCtoDateTimeWithSeconds(response?.data?.effectiveTo),
            tariffName: response?.data?.name,
            tariffType: response?.data?.type,
            currentType:
              response?.data?.currentType || response?.data?.connectorRating?.currentType,
            capacityKW: response?.data?.connectorRating?.capacityKW ?? response?.data?.capacityKW,
            unit: response?.data?.connectorRating?.unit ?? response?.data?.unit,
            accountCustomId: response?.data?.accountCustomId,
            connectorRating: response?.data?.connectorRating,
            scopeLevels: response?.data?.scopeLevels,
            country: parsedScopeLevels?.find((level: any) => level.scope === 'country')?.scopeName,
            state: parsedScopeLevels?.find((level: any) => level.scope === 'state')?.scopeName,
            city: parsedScopeLevels?.find((level: any) => level.scope === 'city')?.scopeName,
            stationId: response?.data?.stationId,
            accountName: response?.data?.accountCustomId,
            scope: response?.data?.scope,
            scopeId: response?.data?.scopeId,
            scopeName: response?.data?.scopeName,
            currency: response?.data?.currency,
            createdBy: ConvertUTCtoDateTimeWithSeconds(response?.data?.createdAt),
            editedBy: ConvertUTCtoDateTimeWithSeconds(response?.data?.updatedAt),
            priceComponents: response?.data?.tariffPricingComponent?.map(
              (comp: any, index: number) => ({
                id: index + 1,
                componentType: comp.componentType,
                componentName: comp.componentName,
                valueType: comp.valueType,
                value: comp.value,
                order: comp.order,
              }),
            ),
            timeBasedPricingSlots: (
              response?.data?.timeBasedPricingDetails ||
              response?.data?.timeBasedPricingSlots ||
              []
            ).map((slot: any, index: number) => ({
              id: index + 1,
              name: slot.name,
              startTime: slot.startTime,
              endTime: slot.endTime,
              recurrenceType: slot.recurrenceType,
              applicableOn:
                slot.dates && slot.dates.length > 0
                  ? slot.dates.map((date: string) => ConvertDDMMYYYYtoISO(date)).join(', ')
                  : '-',
              days: Array.isArray(slot.daysOfWeek) ? slot.daysOfWeek.join(' | ') : '-',
              priceMultiplier: String(slot.priceMultiplier),
            })),
          };
          setTariffDetails(output);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [tariffId, isPartner]);

  if (loading) {
    return (
      <div className="tariff-side-pane-wrap">
        <div className="loader-wrap">
          <BatteryLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="tariff-side-pane-wrap">
      <div className="tariff-side-pane-container">
        <div className="details-section">
          <p className="section-title">{t('basicDetails')}</p>
          <div className="details-grid three-column">
            <div className="detail-item">
              <p className="label">{t('tariffName')}</p>
              <p className="value">{tariffDetails.tariffName || t('na')}</p>
            </div>

            <div className="detail-item">
              <p className="label">{t('tariffType')}</p>
              <p className="value">{tariffDetails.tariffType || t('na')}</p>
            </div>

            <div className="detail-item">
              <p className="label">{t('country')}</p>
              <p className="value">{tariffDetails.country || t('na')}</p>
            </div>

            <div className="detail-item">
              <p className="label">{t('state')}</p>
              <p className="value">{tariffDetails.state || t('na')}</p>
            </div>

            <div className="detail-item">
              <p className="label">{t('city')}</p>
              <p className="value">{tariffDetails.city || t('na')}</p>
            </div>

            <div className="detail-item">
              <p className="label">{t('scopeId')}</p>
              <p className="value">{tariffDetails.scopeId || t('na')}</p>
            </div>

            <div className="detail-item">
              <p className="label">{t('scopeName')}</p>
              <p className="value">{tariffDetails.scopeName || t('na')}</p>
            </div>

            <div className="detail-item">
              <p className="label">{t('createdBy')}</p>
              <p className="value">{tariffDetails.createdBy || t('na')}</p>
            </div>

            <div className="detail-item">
              <p className="label">{t('editedBy')}</p>
              <p className="value">{tariffDetails.editedBy || t('na')}</p>
            </div>
          </div>
        </div>

        <div className="details-section">
          <p className="section-title">{t('technicalDetails')}</p>
          <div className="details-grid two-column">
            <div className="detail-item">
              <p className="label">{t('currentType')}</p>
              <p className="value">{tariffDetails.currentType || t('na')}</p>
            </div>

            <div className="detail-item">
              <p className="label">{t('connectorRatingKw')}</p>
              <p className="value">
                {[tariffDetails.currentType, tariffDetails.capacityKW, tariffDetails.unit]
                  .filter(Boolean)
                  .join(' ') || t('na')}
              </p>
            </div>

            <div className="detail-item">
              <p className="label">{t('enforcementDate')}</p>
              <p className="value">{tariffDetails.enforcementDate || t('na')}</p>
            </div>

            <div className="detail-item">
              <p className="label">{t('expiryDate')}</p>
              <p className="value">{tariffDetails.expiryDate || t('na')}</p>
            </div>
          </div>
        </div>

        <div className="details-section">
          <p className="section-title">{t('priceComponents')}</p>
          <div className="simple-table">
            <div className="simple-table-header">
              <div className="col-1-5">{t('componentType')}</div>
              <div className="col-1-5">{t('componentName')}</div>
              <div className="col-1-5">{t('valueType')}</div>
              <div className="col-1">{t('value')}</div>
              <div className="col-0-5">{t('order')}</div>
            </div>
            {tariffDetails?.priceComponents?.map((comp: any) => (
              <div className="simple-table-row" key={comp.id}>
                <div className="col-1-5">{comp.componentType || t('na')}</div>
                <div className="col-1-5">{comp.componentName || t('na')}</div>
                <div className="col-1-5">{comp.valueType || t('na')}</div>
                <div className="col-1">
                  {comp.value !== undefined && comp.value !== null
                    ? `${Number(comp.value).toFixed(2)} ${tariffDetails.currency || ''}`
                    : t('na')}
                </div>
                <div className="col-0-5">{comp.order ?? t('na')}</div>
              </div>
            ))}
            {(!tariffDetails?.priceComponents || tariffDetails.priceComponents.length === 0) && (
              <div className="simple-table-row">
                <div className="col-1">{t('noPriceComponentsFound')}</div>
              </div>
            )}
          </div>
        </div>

        {false && (
          <div className="details-section">
            <p className="section-title">{t('timeBasedPricingSlots')}</p>
            <div className="pricing-slots-list">
              {!tariffDetails?.timeBasedPricingSlots ||
              tariffDetails.timeBasedPricingSlots.length === 0 ? (
                <div className="pricing-slot-item">
                  <div className="details-grid three-column">
                    <div className="detail-item">
                      <p className="label">{t('slotName')}</p>
                      <p className="value">{t('na')}</p>
                    </div>
                    <div className="detail-item">
                      <p className="label">{t('startTime')}</p>
                      <p className="value">{t('na')}</p>
                    </div>
                    <div className="detail-item">
                      <p className="label">{t('endTime')}</p>
                      <p className="value">{t('na')}</p>
                    </div>
                    <div className="detail-item">
                      <p className="label">{t('recurrenceType')}</p>
                      <p className="value">{t('na')}</p>
                    </div>
                    <div className="detail-item">
                      <p className="label">{t('applicableOn')}</p>
                      <p className="value">{t('na')}</p>
                    </div>
                    <div className="detail-item">
                      <p className="label">{t('days')}</p>
                      <p className="value">{t('na')}</p>
                    </div>
                    <div className="detail-item">
                      <p className="label">{t('multiplier')}</p>
                      <p className="value">{t('na')}</p>
                    </div>
                  </div>
                  <div style={{ marginTop: '24px' }}>
                    <p className="no-data-text">{t('noTimeBasedPricingSlotsFound')}</p>
                  </div>
                </div>
              ) : (
                tariffDetails.timeBasedPricingSlots.map((slot: any, index: number) => (
                  <div className="pricing-slot-item" key={slot.id}>
                    {index > 0 && <hr className="slot-divider" />}
                    <div className="details-grid three-column">
                      <div className="detail-item">
                        <p className="label">{t('slotName')}</p>
                        <p className="value">{slot.name || t('na')}</p>
                      </div>
                      <div className="detail-item">
                        <p className="label">{t('startTime')}</p>
                        <p className="value">{slot.startTime || t('na')}</p>
                      </div>
                      <div className="detail-item">
                        <p className="label">{t('endTime')}</p>
                        <p className="value">{slot.endTime || t('na')}</p>
                      </div>
                      <div className="detail-item">
                        <p className="label">{t('recurrenceType')}</p>
                        <p className="value">{slot.recurrenceType || t('na')}</p>
                      </div>
                      <div className="detail-item">
                        <p className="label">{t('applicableOn')}</p>
                        <p className="value">{slot.applicableOn || t('na')}</p>
                      </div>
                      <div className="detail-item">
                        <p className="label">{t('days')}</p>
                        <p className="value">{slot.days || t('na')}</p>
                      </div>
                      <div className="detail-item">
                        <p className="label">{t('multiplier')}</p>
                        <p className="value">{slot.priceMultiplier || t('na')}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      <div className="sidepanel-footer-actions">
        <MyButton
          label={t('verifyTariff')}
          buttonType="outline"
          onClickFunc={() => {
            navigate(
              `/ev-admin/tariffs/view/${tariffId}?isExtended=true${isPartner ? '&isPartner=true' : ''}`,
            );
          }}
        />
        {tariffDetails?.status?.toLowerCase() !== 'expired' && (
          <MyButton
            label={t('editTariff')}
            buttonType="submit"
            onClickFunc={() =>
              navigate(
                `/ev-admin/tariffs/edit-tariff/${tariffId}${isPartner ? '?isPartner=true' : ''}`,
              )
            }
            disableButton={
              !permissionCheck('partnerTariffUpdate') && !permissionCheck('cpoTariffUpdate')
            }
          />
        )}
      </div>
    </div>
  );
}

TariffSidePanel.defaultProps = {
  fetchIdFrom: 'queryParams',
  id: '',
  isPartner: false,
};

export default TariffSidePanel;
